import React, { useState, useContext, useEffect } from 'react'

import CryptoJS from 'crypto-js';

import { ListItem } from '@mui/material'

import ClientDialog from '../../../Dialogs/Client'
import ActionButton from '../../ActionButton'
import useNotification from '../../../Notification'
import AddIcon from '@mui/icons-material/Add';
import dayjs from 'dayjs'

import api from '../../../../api'

import { ScreenContext } from '../..'
import { Box } from '@mui/material';

const INVALID_PHONE_MESSAGE = 'Telefone inválido. Revise o número informado e tente novamente.'
const INVALID_EMAIL_MESSAGE = 'E-mail inválido. Revise o endereço informado e tente novamente.'

const hasApiErrorMessage = (value, expectedMessage) => {
	if (!value)
		return false

	if (typeof value === 'string')
		return value === expectedMessage

	if (Array.isArray(value))
		return value.some((item) => hasApiErrorMessage(item, expectedMessage))

	if (typeof value === 'object')
		return Object.values(value).some((item) => hasApiErrorMessage(item, expectedMessage))

	return false
}

const isValidEmail = (value = '') => {
	const email = String(value).trim()

	if (!/^[^\s@]+@[^\s@]+$/.test(email))
		return false

	const [localPart, domain] = email.split('@')

	if (!localPart || localPart.startsWith('.') || localPart.endsWith('.') || localPart.includes('..'))
		return false

	if (!domain || domain.includes('..'))
		return false

	const domainLabels = domain.split('.')
	return domainLabels.length > 1 && domainLabels.every((label) => /^[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?$/.test(label))
}

const AddClient = ({ groups, customFields, usersEmails }) => {
	const { updateClients, updateReady, clients, enterpriseId, settings, permissions, updateNumberSelectedModel } = useContext(ScreenContext)
	const [createClientModal, updateCreateClientModal] = useState(false)
	const [clientModel, updateClientModel] = useState([])

	const isNumber = (n) => {
		return !isNaN(parseFloat(n)) && isFinite(n);
	}

	const formatPhone = (telephone) => {
		let newTelephone = '';
		if (telephone) {
			const numberTelephone = telephone.replace(/[^0-9]+/g, '');
			if (numberTelephone.length < 10)
				return false
			if (isNumber(numberTelephone) && numberTelephone.substring(0, 3) !== '+55')
				newTelephone = `+55${numberTelephone}`;
			else
				newTelephone = telephone;
		}
		return newTelephone;
	}

	const generateUniqueNick = async () => {
		const randomString = Math.random().toString(36).substring(2)
		return CryptoJS.MD5(randomString).toString()
	}

	const handleCreateClient = async (name, email, telephone, userFields, advisor, contactEnterprise, cpf, customer_code, groups, toCreateCustomFields, notificationOptOut, owner, birthday, linkedin_url) => {

		const formatTelephone = formatPhone(telephone);
		const formattedName = String(name || '').trim()
		const formattedEmail = String(email || '').trim()
		const hasTelephone = Boolean(String(telephone || '').trim())

		if (!formattedName && !formattedEmail && !hasTelephone) {
			useNotification(
                'Ops!',
                `Insira pelo menos um: nome, email, ou telefone.`,
                'warning'
			)
			return;
		}

		if (hasTelephone && !formatTelephone) {
			useNotification(
				'Atenção!',
				INVALID_PHONE_MESSAGE,
				'warning'
			)
			return
		}

		if (formattedEmail && !isValidEmail(formattedEmail)) {
			useNotification(
				'Atenção!',
				INVALID_EMAIL_MESSAGE,
				'warning'
			)
			return
		}

		updateReady(false)
		updateNumberSelectedModel([])

		const payload = {
			// Setting default nick as telephone for WhatsApp notifications
			nick                 : (hasTelephone) ? formatTelephone : (formattedEmail) ? formattedEmail : await generateUniqueNick(),
			name                 : name,
			email                : formattedEmail,
			telephone            : formatTelephone,
			user_fields          : userFields,
			advisor              : advisor,
			contact_enterprise   : contactEnterprise,
			cpf                  : cpf,
			customer_code        : customer_code,
			notification_opt_out : notificationOptOut,
			groups               : groups,
			owner                : owner === '' ? null : owner,
			birthday             : (birthday) ? dayjs(birthday, "DD/MM/YYYY").format("YYYY-MM-DDTHH:mm") : null,
			linkedin_url         : linkedin_url
		}

		if (toCreateCustomFields.length !== 0)
			payload['create_custom_fields'] = toCreateCustomFields

		api.post(`/api/enterprises/${enterpriseId}/contacts/`, payload)
			.then(async (res) => {
				updateReady(true)

				if (res.status == 201) {

					const client = res.data

					updateClients(prevState => [...prevState, client].sort((x, y) => x.id > y.id ? -1 : 1));
					updateCreateClientModal(false)

					useNotification(
						'Sucesso!',
						'Cliente adicionado',
						'success'
					);

					return;
				}
				throw res;
			})
			.catch(async (err) => {
				updateReady(true)

				if (hasApiErrorMessage(err?.data, 'Nick is a required parameter')) {
					useNotification(
						'Ops!',
						INVALID_PHONE_MESSAGE,
						'danger'
					)
					return
				}

				if (hasApiErrorMessage(err?.data, 'Enter a valid email address.')) {
					useNotification(
						'Ops!',
						INVALID_EMAIL_MESSAGE,
						'danger'
					)
					return
				}

				if (err.data?.details?.email) {

					// Validates duplicated email adress
					if (err.data.details?.email[0].error === 'DuplicateValue') {
						useNotification(
							'Ops!',
							`O e-mail "${email}" já está cadastrado na base do zendesk em outro contato`,
							'danger'
						);

						return;
					}



					// Validates any other error
					useNotification(
						'Ops!',
						err.data.details?.email[0].description,
						'danger'
					);

					return;
				}

				let defaultError = 'Ocorreu um erro no sistema'
				if (err.data?.error?.cpf) {
					defaultError = err.data.error.cpf[0]
				}
				else if (err.data?.error && err.data?.contact) {
					const name = err.data.contact.owner_name ? err.data.contact.owner_name : 'Público'
					defaultError = `Este contato já existe - pertence a ${name} criado em ${dayjs(err.data.contact.created_at).format('DD/MM/YYYY')}. `
			
				}
				else if (Array.isArray(err.data?.error) && err.data.error.includes('Enter a valid email address.')) {
					defaultError = 'Utilize um e-mail válido.'
				}
				else if (err.status == 400) {
		            const errorMessages = {
		                'Invalid data type. Expected an integer.'    : 'deve conter um número.',
		                'Invalid data type. Expected either 0 or 1.' : 'deve conter Sim ou Não.',
		            };
		            Object.keys(err.data).forEach((key) => {
		                err.data[key].forEach((error) => {
		                    if (errorMessages[error])
		                        defaultError = `O campo ${key} ${errorMessages[error]}`;
		                });
		            })
		        }

				if (err.status === 406) {
					defaultError = 'Limite de contatos atingido. Contate o seu gerente comercial.';
				} else if (err.status === 405)
					defaultError = 'Você não pode criar contatos.';

				// Fallback for unknown errors
				useNotification(
					'Ops!',
					defaultError,
					'danger'
				);
				console.error(err);
			});
	}

	return (
		<>
			<ListItem className={'menuItem'} sx={{ padding: 0 }} >
				{createClientModal &&
					<ClientDialog open={true}
						title="Novo contato"
						type="create"
						model={clientModel}
						key={'ClientCreate'}
						buttonText="Adicionar"
						handleClose={() => updateCreateClientModal(false)}
						submit={handleCreateClient}
						is_financial_market_company={settings.is_financial_market_company}
						isAdmin={permissions.isAdmin}
						listOfUsers={[...usersEmails].sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`, 'pt-BR', { sensitivity: 'base' }))}
						groups={groups}
						customFields={customFields}
					/>}
				<Box sx={{ width: "100%", padding: ".5rem", marginRight: "1.5rem" }} onClick={() => updateCreateClientModal(true)}>Novo contato</Box>
			</ListItem>
		</>
	)
}

export default AddClient
